import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Button, 
  useTheme, 
  useMediaQuery, 
  Card, 
  CardContent,
  Paper,
  alpha,
  Fade,
  Chip
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import ExploreIcon from '@mui/icons-material/Explore';
import ForumIcon from '@mui/icons-material/Forum';
import CatchingPokemonIcon from '@mui/icons-material/CatchingPokemon';
import AssistantIcon from '@mui/icons-material/Assistant';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';
import ShieldIcon from '@mui/icons-material/Shield';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SEO from '../components/SEO';

export default function HomePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const features = [
    {
      icon: <BarChartIcon sx={{ fontSize: { xs: 40, md: 54 }, color: '#24CC9F' }} />,
      title: "Live VGC Rankings",
      description:
        "Monthly-updated usage rankings for every Pokémon in the VGC metagame. Track popularity shifts, discover rising threats, and explore historical trends across past seasons."
    },
    {
      icon: <AutoGraphIcon sx={{ fontSize: { xs: 40, md: 54 }, color: '#24CC9F' }} />,
      title: "Deep Win-Rate Analysis",
      description:
        "Go beyond usage — compare win rates, top movesets, popular abilities, common items, tera types, and ideal teammates for any Pokémon. Understand WHY the best strategies work."
    },
    {
      icon: <OndemandVideoIcon sx={{ fontSize: { xs: 40, md: 54 }, color: '#24CC9F' }} />,
      title: "Battle Replay Library",
      description:
        "Browse thousands of indexed Pokémon Showdown VGC replays. Filter by player, rating, date, or format. Analyze each match turn-by-turn to study top-level decision-making."
    },
    {
      icon: <AssistantIcon sx={{ fontSize: { xs: 40, md: 54 }, color: '#24CC9F' }} />,
      title: "Turn Assistant",
      description:
        "Set up any Double Battle board state and get instant move recommendations based on real competitive data. Damage calcs, speed comparisons, and optimal play suggestions — all in one tool."
    },
    {
      icon: <CatchingPokemonIcon sx={{ fontSize: { xs: 40, md: 54 }, color: '#24CC9F' }} />,
      title: "Full Pokémon Database",
      description:
        "Explore all 1025 Pokémon with base stats, type matchups, abilities, moves, and alternate forms. Everything you need for teambuilding, from base Pokémon to regional variants."
    },
    {
      icon: <ForumIcon sx={{ fontSize: { xs: 40, md: 54 }, color: '#24CC9F' }} />,
      title: "Community Forum",
      description:
        "Discuss strategies, share team reports, ask for rate-my-team advice, and stay up to date with metagame shifts in our community forum."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Explore the Metagame",
      description: "Start with the Rankings page to see which Pokémon are dominating the current VGC format. Check usage rates, win percentages, and common sets."
    },
    {
      step: "2",
      title: "Study Real Matches",
      description: "Browse Public Games to find replays from competitive players. Use the turn-by-turn analysis to understand the reasoning behind each move."
    },
    {
      step: "3",
      title: "Practice With Data",
      description: "Use the Turn Assistant to practice decision-making. Set up scenarios you struggle with and learn the statistically optimal plays."
    },
    {
      step: "4",
      title: "Track Your Progress",
      description: "Save your replays, analyze your team's performance with Battle Analytics, and identify patterns in your wins and losses to improve consistently."
    }
  ];

  const vgcConcepts = [
    {
      icon: <ShieldIcon />,
      title: "Type Matchups",
      description: "Pokémon VGC is built on the type chart — 18 types with super-effective, not-very-effective, and immunity interactions. Knowing these by heart is the foundation of every good play."
    },
    {
      icon: <SpeedIcon />,
      title: "Speed Control",
      description: "Who moves first determines the entire turn. Tailwind doubles your team's speed, Trick Room inverts the order, and priority moves bypass speed entirely. Mastering speed control wins games."
    },
    {
      icon: <GroupsIcon />,
      title: "Team Synergy",
      description: "In Doubles, your two active Pokémon must complement each other. Redirecting attacks with Follow Me, boosting allies with Helping Hand, or setting weather for your partner's abilities — synergy is everything."
    },
    {
      icon: <PsychologyIcon />,
      title: "Prediction & Reads",
      description: "VGC rewards players who predict their opponent's moves. Will they Protect? Switch? Double into your slot? Reading the game correctly is what separates top players from the rest."
    }
  ];

  const quickLinks = [
    { 
      icon: <TrendingUpIcon />, 
      title: "VGC Rankings", 
      description: "See real-time usage stats and discover the top Pokémon in the current format",
      path: "/rankings" 
    },
    { 
      icon: <ExploreIcon />, 
      title: "Watch Replays", 
      description: "Browse competitive matches, filter by player or format, and study real battles",
      path: "/public-games" 
    },
    { 
      icon: <CatchingPokemonIcon />, 
      title: "Pokémon Database", 
      description: "Look up any Pokémon — stats, types, abilities, moves, and matchup charts",
      path: "/pokemon-list" 
    },
    { 
      icon: <AssistantIcon />, 
      title: "Turn Assistant", 
      description: "Get data-driven move recommendations for any VGC board state",
      path: "/turn-assistant" 
    }
  ];

  return (
    <>
      <SEO 
        title="Pokémon VGC Rankings, Pokémon List, Turn Assistant & Competitive Stats"
        description="Trainer Academy is the free Pokémon VGC analytics platform. Live Pokémon usage rankings, complete Pokémon list and database, turn assistant battle advisor, battle replay analysis, team composition stats, and competitive VGC strategy tools powered by real data."
        keywords="pokemon vgc, competitive pokemon, pokemon rankings, ranking pokemon, pokemon statistics, vgc stats, pokemon list, pokemon database, pokemon turn assistant, battle advisor, vgc metagame, pokemon win rates, vgc team builder, pokemon usage stats, vgc pokemon list, pokemon competitive, pokemon strategy"
      />
      {/* Banner region */}
      <Box component="header" role="banner">
        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            minHeight: { xs: '75vh', sm: '80vh', md: '90vh' },
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingX: { xs: 2, sm: 4, md: 6 },
            py: { xs: 2, sm: 3, md: 0 }
          }}
        >
          <Container maxWidth="md">
            <Fade in={true} timeout={1000}>
              <Box
                sx={{
                  bgcolor: alpha('#000000', 0.6),
                  color: '#ffffff',
                  p: { xs: 3, sm: 4, md: 5 },
                  textAlign: 'center',
                  borderRadius: 2,
                  backdropFilter: 'blur(5px)',
                }}
              >
                <Typography 
                  variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h2'} 
                  component="h1"
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  Pokémon VGC Rankings, Pokémon List & Turn Assistant
                </Typography>
                <Typography 
                  variant="body1"
                  component="p"
                  sx={{ mb: 2, maxWidth: '700px', mx: 'auto' }}
                >
                  Trainer Academy is the free analytics platform for competitive Pokémon VGC. Live Pokémon usage rankings, a complete Pokémon list and database, real-time turn assistant battle advisor, replay analysis, and everything you need to climb the ladder — powered by real competitive data.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size={isMobile ? 'medium' : 'large'}
                    startIcon={<TrendingUpIcon />}
                    onClick={() => navigate('/rankings')}
                    sx={{ 
                      py: { xs: 1, md: 1.5 },
                      px: { xs: 3, md: 4 },
                      fontWeight: 'bold'
                    }}
                  >
                    View Rankings
                  </Button>
                  <Button
                    variant="outlined"
                    size={isMobile ? 'medium' : 'large'}
                    color="secondary"
                    startIcon={<ExploreIcon />}
                    onClick={() => navigate('/public-games')}
                    sx={{ 
                      py: { xs: 1, md: 1.5 },
                      px: { xs: 3, md: 4 },
                      borderColor: '#ffffff',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#ffffff',
                        backgroundColor: alpha('#ffffff', 0.1)
                      }
                    }}
                  >
                    Explore Replays
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Container>
        </Box>
      </Box>

      {/* Main content region */}
      <Box component="main" role="main" aria-labelledby="home-title">
        <Typography
          id="home-title"
          component="h1"
          variant="h3"
          sx={{ position: 'absolute', width: 1, height: 1, clip: 'rect(0,0,0,0)' }}
        >
          Trainer Academy — Pokémon VGC Rankings, Pokémon List, Turn Assistant & Competitive Stats
        </Typography>

        {/* What is Trainer Academy Section */}
        <Box 
          component="section" 
          aria-labelledby="about-heading"
          sx={{ 
            py: { xs: 5, md: 8 }, 
            px: { xs: 2, sm: 4, md: 6 },
            backgroundColor: alpha('#221FC7', 0.95)
          }}
        >
          <Container maxWidth="md">
            <Typography 
              id="about-heading"
              variant={isMobile ? 'h5' : 'h4'} 
              component="h2"
              align="center" 
              gutterBottom
              sx={{ color: '#ffffff', fontWeight: 'bold', mb: 3 }}
            >
              Why Trainer Academy?
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, maxWidth: '750px', mx: 'auto', lineHeight: 1.8 }}
            >
              Pokémon VGC is evolving into one of the most strategic gaming scenes. With thousands of team
              combinations and a shifting metagame, staying competitive requires data-driven tools — usage
              rankings, a searchable Pokémon list (database), turn assistant advice, and replay analysis.
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ color: 'rgba(255,255,255,0.9)', mb: 4, maxWidth: '750px', mx: 'auto', lineHeight: 1.8 }}
            >
              Trainer Academy provides live Pokémon rankings, a complete Pokémon list and database, a
              real-time turn assistant (battle advisor and VGC calculator), and detailed battle analytics so you
              can research Pokémon, build stronger teams, and make optimal in-game decisions.
            </Typography>
          </Container>
        </Box>

        {/* Key Features Section - 6 cards */}
        <Box 
          component="section" 
          aria-labelledby="features-heading"
          sx={{ 
            py: { xs: 5, md: 8 }, 
            px: { xs: 2, sm: 4, md: 6 },
            backgroundColor: '#221FC7'
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              id="features-heading"
              variant={isMobile ? 'h5' : 'h4'} 
              component="h2"
              align="center" 
              gutterBottom
              sx={{ color: '#ffffff', fontWeight: 'bold', mb: 1 }}
            >
              Everything You Need to Compete
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, maxWidth: '600px', mx: 'auto' }}
            >
              Six integrated tools designed for VGC players — from beginner to Worlds-level.
            </Typography>
            
            <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
              {features.map((feature, idx) => (
                <Grid key={idx} item xs={12} sm={6} md={4}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      backgroundColor: alpha('#ffffff', 0.1), 
                      color: '#ffffff',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0, 0, 0, 0.2)'
                      },
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: { xs: 3, md: 4 } }}>
                      <Box sx={{ mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography 
                        variant="h6" 
                        component="h3"
                        sx={{ mb: 1.5, fontWeight: 'bold' }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.8)">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* How It Works Section */}
        <Box 
          component="section" 
          aria-labelledby="how-it-works-heading"
          sx={{ 
            py: { xs: 5, md: 8 }, 
            px: { xs: 2, sm: 4, md: 6 },
            backgroundColor: alpha('#1a1a2e', 0.95)
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              id="how-it-works-heading"
              variant={isMobile ? 'h5' : 'h4'} 
              component="h2"
              align="center" 
              gutterBottom
              sx={{ color: '#ffffff', fontWeight: 'bold', mb: 1 }}
            >
              How It Works
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, maxWidth: '600px', mx: 'auto' }}
            >
              From exploring the metagame to tracking your own improvement — here's the path.
            </Typography>
            
            <Grid container spacing={3}>
              {howItWorks.map((step, idx) => (
                <Grid key={idx} item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', px: 2 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: '#24CC9F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '1.5rem'
                      }}
                    >
                      {step.step}
                    </Box>
                    <Typography variant="h6" component="h3" sx={{ color: '#ffffff', fontWeight: 'bold', mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* VGC Concepts Section */}
        <Box 
          component="section" 
          aria-labelledby="vgc-concepts-heading"
          sx={{ 
            py: { xs: 5, md: 8 }, 
            px: { xs: 2, sm: 4, md: 6 },
            backgroundColor: '#221FC7'
          }}
        >
          <Container maxWidth="lg">
            <Chip 
              label="LEARN VGC" 
              sx={{ 
                mb: 2, 
                backgroundColor: '#24CC9F', 
                color: '#fff', 
                fontWeight: 'bold',
                display: 'flex',
                width: 'fit-content',
                mx: 'auto'
              }} 
            />
            <Typography 
              id="vgc-concepts-heading"
              variant={isMobile ? 'h5' : 'h4'} 
              component="h2"
              align="center" 
              gutterBottom
              sx={{ color: '#ffffff', fontWeight: 'bold', mb: 1 }}
            >
              Key VGC Concepts Every Player Should Know
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, maxWidth: '650px', mx: 'auto' }}
            >
              Whether you're preparing for your first Local or pushing for a Worlds invite, these fundamentals matter.
            </Typography>
            
            <Grid container spacing={3}>
              {vgcConcepts.map((concept, idx) => (
                <Grid key={idx} item xs={12} sm={6}>
                  <Paper
                    sx={{
                      p: 3,
                      height: '100%',
                      backgroundColor: alpha('#ffffff', 0.08),
                      color: '#ffffff',
                      borderRadius: 2,
                      borderLeft: '4px solid #24CC9F'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                      <Box sx={{ color: '#24CC9F' }}>{concept.icon}</Box>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                        {concept.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                      {concept.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/pokemon-list')}
                sx={{
                  borderColor: '#24CC9F',
                  color: '#24CC9F',
                  '&:hover': {
                    borderColor: '#24CC9F',
                    backgroundColor: alpha('#24CC9F', 0.1)
                  }
                }}
              >
                Browse the Pokémon Database
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Quick Links Section */}
        <Box 
          component="section" 
          aria-labelledby="quick-links-heading"
          sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, sm: 4, md: 6 } }}
        >
          <Container maxWidth="lg">
            <Typography 
              id="quick-links-heading"
              variant={isMobile ? 'h5' : 'h4'} 
              component="h2"
              align="center" 
              gutterBottom
              sx={{ mb: 4, color: '#ffffff', fontWeight: 'bold' }}
            >
              Start Training Now
            </Typography>
            
            <Grid container spacing={3}>
              {quickLinks.map((link, idx) => (
                <Grid key={idx} item xs={12} sm={6} md={3}>
                  <Paper 
                    elevation={3}
                    sx={{ 
                      p: 3, 
                      height: '100%',
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, background-color 0.2s',
                      backgroundColor: '#221FC7',
                      color: '#ffffff',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        backgroundColor: '#24CC9F'
                      }
                    }}
                    onClick={() => navigate(link.path)}
                    component="article"
                    aria-label={`Navigate to ${link.title}`}
                  >
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: '50%', 
                        backgroundColor: alpha('#ffffff', 0.1),
                        mb: 2
                      }}
                    >
                      {link.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {link.title}
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.8)">
                      {link.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* Footer region with internal links for SEO */}
      <Box 
        component="footer" 
        role="contentinfo" 
        sx={{ 
          py: { xs: 4, md: 6 }, 
          px: { xs: 2, sm: 4, md: 6 },
          backgroundColor: alpha('#000000', 0.3),
          color: 'rgba(255,255,255,0.7)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 'bold', mb: 2 }}>
                Trainer Academy
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                The competitive Pokémon VGC analytics platform. Built by players, for players. 
                We turn competitive battle data into insights that help you improve.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                © {new Date().getFullYear()} Trainer Academy
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ color: '#24CC9F', fontWeight: 'bold', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Tools
              </Typography>
              <nav aria-label="Footer tools navigation">
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, '& li': { mb: 1 } }}>
                  <li><Typography component={Link} to="/rankings" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#24CC9F' } }}>Rankings</Typography></li>
                  <li><Typography component={Link} to="/public-games" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#24CC9F' } }}>Public Games</Typography></li>
                  <li><Typography component={Link} to="/battle-analytics" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#24CC9F' } }}>Battle Analytics</Typography></li>
                  <li><Typography component={Link} to="/turn-assistant" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#24CC9F' } }}>Turn Assistant</Typography></li>
                </Box>
              </nav>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ color: '#24CC9F', fontWeight: 'bold', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Resources
              </Typography>
              <nav aria-label="Footer resources navigation">
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, '& li': { mb: 1 } }}>
                  <li><Typography component={Link} to="/pokemon-list" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#24CC9F' } }}>Pokémon Database</Typography></li>
                  <li><Typography component={Link} to="/forum" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#24CC9F' } }}>Community Forum</Typography></li>
                  <li><Typography component={Link} to="/saved-games" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#24CC9F' } }}>Saved Games</Typography></li>
                </Box>
              </nav>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ color: '#24CC9F', fontWeight: 'bold', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                About
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                Pokémon and Pokémon character names are trademarks of Nintendo, The Pokémon Company, and Game Freak. Trainer Academy is a fan project and is not affiliated with or endorsed by these companies.
              </Typography>
              <Typography component={Link} to="/contact" variant="body2" sx={{ color: '#24CC9F', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Contact & Help →
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
